(() => {
  const ARTICLE_SELECTOR = '.article--gallery-post';
  const CONTENT_SELECTOR = '.article__content';
  const INITIALIZED_ATTRIBUTE = 'data-article-gallery-initialized';
  const galleryStates = new WeakMap();

  const hasMeaningfulText = (element) => {
    const clone = element.cloneNode(true);
    clone.querySelectorAll('img, source, br').forEach((node) => node.remove());
    return clone.textContent.replace(/\u00a0/g, ' ').trim() !== '';
  };

  const getMovableMedia = (image, container) => {
    const link = image.closest('a');
    if (link && container.contains(link) && link.querySelectorAll('img').length === 1) return link;

    const picture = image.closest('picture');
    if (picture && container.contains(picture) && picture.querySelectorAll('img').length === 1) return picture;

    return image;
  };

  const getImageRecords = (node) => {
    if (!(node instanceof Element)) return [];

    const images = node.matches('img') ? [node] : Array.from(node.querySelectorAll('img'));
    if (images.length === 0) return [];
    if (node.querySelector('video, iframe, canvas, svg, audio, table, figcaption')) return [];
    if (hasMeaningfulText(node)) return [];

    if (images.length === 1) {
      return [{ element: node, image: images[0] }];
    }

    return images.map((image) => {
      const element = document.createElement('div');
      element.appendChild(getMovableMedia(image, node));
      return { element, image };
    });
  };

  const getSetSizes = (count) => {
    if (count <= 4) return [count];

    const sizes = [];
    let remaining = count;

    while (remaining > 4) {
      sizes.push(3);
      remaining -= 3;
    }

    if (remaining > 0) sizes.push(remaining);
    return sizes;
  };

  const getLargestSrcsetCandidate = (image) => {
    const srcset = image.getAttribute('srcset');
    if (!srcset) return '';

    const candidates = srcset
      .split(',')
      .map((candidate) => {
        const parts = candidate.trim().split(/\s+/);
        const descriptor = parts[1] || '';
        const width = descriptor.endsWith('w') ? Number.parseInt(descriptor, 10) : 0;
        return { src: parts[0], width };
      })
      .filter((candidate) => candidate.src);

    candidates.sort((a, b) => b.width - a.width);
    return candidates[0]?.src || '';
  };

  const getImageSource = ({ element, image }) => {
    const link = image.closest('a');
    const linkedSource = link && element.contains(link) ? link.href : '';
    const linkedSourceIsImage = /\.(?:avif|gif|jpe?g|png|webp)(?:$|[?#])/i.test(linkedSource);

    return (
      (linkedSourceIsImage && linkedSource) ||
      image.dataset.originalSrc ||
      getLargestSrcsetCandidate(image) ||
      image.currentSrc ||
      image.src
    );
  };

  const createDataSource = (records) =>
    records.map((record) => {
      const { image } = record;
      const attributeWidth = Number.parseInt(image.getAttribute('width'), 10);
      const attributeHeight = Number.parseInt(image.getAttribute('height'), 10);
      const renderedRatio = image.width && image.height ? image.width / image.height : 1;
      const width = image.naturalWidth || attributeWidth || 1600;
      const height = image.naturalHeight || attributeHeight || Math.round(width / renderedRatio);

      return {
        src: getImageSource(record),
        msrc: image.currentSrc || image.src,
        width,
        height,
        alt: image.alt || '',
      };
    });

  const createLightbox = (state) => {
    if (!window.FoxTheme?.PhotoSwipeLightbox || !window.FoxTheme?.PhotoSwipe) return null;

    const lightbox = new window.FoxTheme.PhotoSwipeLightbox({
      dataSource: createDataSource(state.records),
      pswpModule: window.FoxTheme.PhotoSwipe,
      bgOpacity: 1,
    });

    lightbox.addFilter('thumbEl', (thumbElement, itemData, index) => {
      return state.records[index]?.image || thumbElement;
    });

    lightbox.addFilter('placeholderSrc', (placeholderSource, itemData, index) => {
      const image = state.records[index]?.image;
      return image?.currentSrc || image?.src || placeholderSource;
    });

    lightbox.init();
    return lightbox;
  };

  const openLightbox = (gallery, index) => {
    const state = galleryStates.get(gallery);
    if (!state) return;

    if (!state.lightbox) state.lightbox = createLightbox(state);
    state.lightbox?.loadAndOpen(index);
  };

  const configureTrigger = (record, index) => {
    const { element, image } = record;
    const existingLink = image.closest('a');
    const label = image.alt ? `Open image: ${image.alt}` : `Open image ${index + 1}`;
    let trigger = existingLink && element.contains(existingLink) ? existingLink : null;

    if (!trigger) {
      trigger = element;
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
    }

    trigger.classList.add('article-gallery__trigger');
    trigger.dataset.galleryIndex = String(index);
    if (!trigger.hasAttribute('aria-label')) trigger.setAttribute('aria-label', label);
  };

  const bindGallery = (gallery, records) => {
    galleryStates.set(gallery, { records, lightbox: null });

    gallery.addEventListener('click', (event) => {
      const trigger = event.target.closest('.article-gallery__trigger');
      if (!trigger || !gallery.contains(trigger)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!window.FoxTheme?.PhotoSwipeLightbox || !window.FoxTheme?.PhotoSwipe) return;

      event.preventDefault();
      openLightbox(gallery, Number.parseInt(trigger.dataset.galleryIndex, 10) || 0);
    });

    gallery.addEventListener('keydown', (event) => {
      const trigger = event.target.closest('.article-gallery__trigger[role="button"]');
      if (!trigger || !gallery.contains(trigger) || !['Enter', ' '].includes(event.key)) return;

      event.preventDefault();
      openLightbox(gallery, Number.parseInt(trigger.dataset.galleryIndex, 10) || 0);
    });
  };

  const createGallery = (records, galleryIndex) => {
    const gallery = document.createElement('div');
    const setSizes = getSetSizes(records.length);
    let recordIndex = 0;

    gallery.className = 'article-gallery';
    gallery.setAttribute('role', 'region');
    gallery.setAttribute('aria-label', `Article image gallery ${galleryIndex + 1}`);

    setSizes.forEach((size, setIndex) => {
      const set = document.createElement('div');
      set.className = `article-gallery__set article-gallery__set--${['one', 'two', 'three', 'four'][size - 1]}`;

      if (size === 3 && setIndex % 2 === 1) {
        set.classList.add('article-gallery__set--feature-right');
      }

      records.slice(recordIndex, recordIndex + size).forEach((record, indexInSet) => {
        const absoluteIndex = recordIndex + indexInSet;
        record.element.classList.add('article-gallery__item', 'blocks-radius');
        if (absoluteIndex === 0) record.element.classList.add('article-gallery__item--mobile-feature');
        configureTrigger(record, absoluteIndex);
        set.appendChild(record.element);
      });

      gallery.appendChild(set);
      recordIndex += size;
    });

    if (records.length % 2 === 0) {
      records.at(-1).element.classList.add('article-gallery__item--mobile-wide');
    }

    bindGallery(gallery, records);
    return gallery;
  };

  const initializeContent = (content) => {
    if (content.hasAttribute(INITIALIZED_ATTRIBUTE)) return;

    let currentRun = [];
    let galleryIndex = 0;

    const flushRun = () => {
      if (currentRun.length > 1) {
        const insertionMarker = document.createComment('article-gallery');
        currentRun[0].element.before(insertionMarker);
        const gallery = createGallery(currentRun, galleryIndex);
        insertionMarker.replaceWith(gallery);
        galleryIndex += 1;
      }
      currentRun = [];
    };

    Array.from(content.childNodes).forEach((node) => {
      const records = getImageRecords(node);

      if (records.length === 1) {
        currentRun.push(records[0]);
        return;
      }

      if (records.length > 1) {
        flushRun();
        const gallery = createGallery(records, galleryIndex);
        node.before(gallery);
        node.remove();
        galleryIndex += 1;
        return;
      }

      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() === '') return;
      if (node.nodeType === Node.COMMENT_NODE) return;
      flushRun();
    });

    flushRun();
    content.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
  };

  const initializeAll = (scope = document) => {
    const articles = scope.matches?.(ARTICLE_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(ARTICLE_SELECTOR) || []);

    articles.forEach((article) => {
      const content = article.querySelector(CONTENT_SELECTOR);
      if (content) initializeContent(content);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
